import {
  ChevronLeft,
  ChevronRight,
  FilterAltOff,
  RemoveShoppingCart,
  Search,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Fade,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useToastStore } from "../../../stores";
import { colorPrimary } from "../../../theme";
import { getFirstDayOfMonth } from "../../../utils/methods/inititalDateTimeFilter";
import { fecthRevendasSemComprasSintetico } from "../repository/AdminRepository";
import { RevendasSemComprasData } from "../types/types";

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const AnimatedBox = styled.div<{ $delay?: number }>`
  animation: ${slideUp} 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  animation-delay: ${({ $delay }) => $delay ?? 0}ms;
  opacity: 0;
`;

interface KpiCardProps {
  label: string;
  value: string;
  subtitle: string;
  icon: React.ReactNode;
  accentColor: string;
  bgColor: string;
  delay: number;
}

function KpiCard({ label, value, subtitle, icon, accentColor, bgColor, delay }: KpiCardProps) {
  return (
    <AnimatedBox $delay={delay}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          position: "relative",
          overflow: "hidden",
          border: "1px solid #f1f5f9",
          height: "100%",
          transition: "all 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 12px 40px rgba(0,0,0,0.08)",
            borderColor: accentColor + "40",
          },
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Typography
            fontSize="0.7rem"
            fontWeight={700}
            textTransform="uppercase"
            letterSpacing={1.5}
            color="#94a3b8"
          >
            {label}
          </Typography>
          <Box
            sx={{
              bgcolor: bgColor,
              color: accentColor,
              p: 1,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {icon}
          </Box>
        </Box>
        <Box display="flex" alignItems="baseline" gap={1}>
          <Typography
            fontSize="2.5rem"
            fontWeight={900}
            letterSpacing={-2}
            color={accentColor}
            lineHeight={1}
          >
            {value}
          </Typography>
          <Typography fontSize="0.75rem" fontWeight={600} color={accentColor + "99"}>
            {subtitle}
          </Typography>
        </Box>
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            height: 3,
            bgcolor: accentColor + "30",
          }}
        />
      </Paper>
    </AnimatedBox>
  );
}

function KpiCardSkeleton({ delay }: { delay: number }) {
  return (
    <AnimatedBox $delay={delay}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          position: "relative",
          overflow: "hidden",
          border: "1px solid #f1f5f9",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Skeleton variant="text" width={100} height={16} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rounded" width={36} height={36} sx={{ borderRadius: 2 }} />
        </Box>
        <Box display="flex" alignItems="baseline" gap={1}>
          <Skeleton variant="text" width={80} height={48} sx={{ borderRadius: 1 }} />
          <Skeleton variant="text" width={60} height={14} sx={{ borderRadius: 1 }} />
        </Box>
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            height: 3,
            bgcolor: "#e2e8f0",
          }}
        />
      </Paper>
    </AnimatedBox>
  );
}

function TableRowSkeleton() {
  return (
    <TableRow>
      {Array.from({ length: 6 }).map((_, i) => (
        <TableCell key={i} sx={{ py: 2.2, borderBottom: "1px solid #f8fafc" }}>
          <Skeleton
            variant="text"
            width={i === 1 ? 200 : i === 3 ? 180 : i === 4 ? 200 : 100}
            height={20}
            sx={{ borderRadius: 1 }}
          />
        </TableCell>
      ))}
    </TableRow>
  );
}

const ITEMS_PER_PAGE = 10;

function formatNumber(val: number | null): string {
  if (val == null) return "0";
  return val.toLocaleString("pt-BR");
}

const inputSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: "white",
    borderRadius: 2,
    fontSize: "0.85rem",
    "& fieldset": { border: "1px solid #e2e8f0" },
    "&:hover fieldset": { borderColor: colorPrimary + "80" },
    "&.Mui-focused fieldset": { borderColor: colorPrimary },
  },
} as const;

const labelSx = {
  component: "label" as const,
  fontSize: "0.7rem",
  fontWeight: 700,
  textTransform: "uppercase" as const,
  letterSpacing: 1,
  color: "#64748b",
  mb: 0.5,
  display: "block",
};

export function RevendasSemComprasSinteticoTab() {
  const [search, setSearch] = useState("");
  const [dataInicio, setDataInicio] = useState(getFirstDayOfMonth());
  const [dataFim, setDataFim] = useState(new Date().toISOString().split("T")[0]);
  const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState<RevendasSemComprasData | null>(null);
  const [loading, setLoading] = useState(true);
  const { setToast } = useToastStore();
  const jaCarregouRef = useRef(false);

  const revendas = data?.revendas ?? [];
  const totalPages = Math.max(1, Math.ceil(revendas.length / ITEMS_PER_PAGE));
  const paginated = revendas.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await fecthRevendasSemComprasSintetico(
        search.trim() || null,
        dataInicio,
        dataFim,
      );
      if (!result?.success) {
        setToast({ variant: "error", message: result.message });
        return;
      }
      setData(result.data);
      setCurrentPage(1);
    } catch {
      setToast({ variant: "error", message: "Erro. Contate o administrador." });
    } finally {
      setLoading(false);
    }
  }, [search, dataInicio, dataFim, setToast]);

  useEffect(() => {
    if (jaCarregouRef.current) return;
    jaCarregouRef.current = true;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetFilters = useCallback(async () => {
    setSearch("");
    setDataInicio(getFirstDayOfMonth());
    setDataFim(new Date().toISOString().split("T")[0]);
    await fetchData();
  }, []);

  return (
    <Fade in timeout={400}>
      <Box display="flex" flexDirection="column" gap={4}>
        {/* FILTROS */}
        <AnimatedBox $delay={0}>
          <Paper
            elevation={0}
            sx={{ p: 3.5, borderRadius: 3, border: "1px solid #f1f5f9" }}
          >
            <Typography
              fontSize="0.7rem"
              fontWeight={700}
              textTransform="uppercase"
              letterSpacing={1.5}
              color="#94a3b8"
              mb={2.5}
            >
              Filtros de Revendas Sem Compras
            </Typography>

            <Box
              display="grid"
              gridTemplateColumns={{
                xs: "1fr",
                sm: "1fr 1fr",
                lg: "1fr 1fr 0.7fr 0.5fr",
              }}
              gap={2}
              alignItems="end"
            >
              <Box>
                <Typography {...labelSx}>Busca</Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Buscar por revenda, documento, e-mail..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: 18, color: "#94a3b8" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={inputSx}
                />
              </Box>

              <Box>
                <Typography {...labelSx}>Período</Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    value={dataInicio}
                    onChange={(e) => setDataInicio(e.target.value)}
                    sx={inputSx}
                  />
                  <Typography fontSize="0.75rem" color="#94a3b8" flexShrink={0}>
                    até
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    value={dataFim}
                    onChange={(e) => setDataFim(e.target.value)}
                    sx={inputSx}
                  />
                </Box>
              </Box>

              <Button
                variant="outlined"
                startIcon={<Search sx={{ fontSize: "17px !important" }} />}
                disableElevation
                disabled={loading}
                onClick={fetchData}
                sx={{
                  color: colorPrimary,
                  borderColor: "#e2e8f0",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  textTransform: "none",
                  borderRadius: 2,
                  px: 3,
                  height: 40,
                  whiteSpace: "nowrap",
                  bgcolor: "white",
                  "&:hover": {
                    borderColor: colorPrimary,
                    bgcolor: colorPrimary + "08",
                  },
                  transition: "all 0.2s",
                }}
              >
                Gerar
              </Button>
              <Button
              variant="text"
              startIcon={<FilterAltOff sx={{ fontSize: "17px !important" }} />}
              disabled={loading}
              onClick={resetFilters}
              sx={{
                color: "#94a3b8",
                fontWeight: 600,
                fontSize: "0.85rem",
                textTransform: "none",
                borderRadius: 2,
                px: 2,
                height: 40,
                whiteSpace: "nowrap",
                "&:hover": {
                  color: colorPrimary,
                  bgcolor: colorPrimary + "08",
                },
                transition: "all 0.2s",
              }}
            >
              Limpar
            </Button>
            </Box>
          </Paper>
        </AnimatedBox>

        {/* KPI CARD */}
        <Box display="grid" gridTemplateColumns="1fr" gap={2.5} maxWidth={400}>
          {loading ? (
            <KpiCardSkeleton delay={80} />
          ) : (
            <KpiCard
              label="Revendas Sem Compras"
              value={formatNumber(data?.qtd_revendas ?? 0)}
              subtitle="no período selecionado"
              icon={<RemoveShoppingCart />}
              accentColor="#ef4444"
              bgColor="#fef2f2"
              delay={80}
            />
          )}
        </Box>

        {/* TABELA */}
        <AnimatedBox $delay={300}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              overflow: "hidden",
              border: "1px solid #f1f5f9",
              boxShadow: "0 4px 24px rgba(0,0,0,0.04)",
            }}
          >
            <Box
              px={3.5}
              py={3}
              display="flex"
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              flexDirection={{ xs: "column", md: "row" }}
              gap={2}
            >
              <Box>
                <Typography fontWeight={800} fontSize="1.15rem" color="#1e293b">
                  Revendas Sem Compras
                </Typography>
                <Typography fontSize="0.82rem" color="#94a3b8" fontWeight={500}>
                  Revendas que não realizaram compras no período
                </Typography>
              </Box>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f8fafc" }}>
                    {[
                      "ID Revenda",
                      "Revenda",
                      "Documento",
                      "Contato",
                      "E-mail",
                      "Localidade",
                    ].map((col) => (
                      <TableCell
                        key={col}
                        sx={{
                          fontSize: "0.68rem",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: 1.5,
                          color: "#94a3b8",
                          borderBottom: "2px solid #f1f5f9",
                          py: 2,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {col}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    Array.from({ length: 5 }).map((_, i) => <TableRowSkeleton key={i} />)
                  ) : paginated.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ py: 6, textAlign: "center" }}>
                        <Typography fontSize="0.9rem" color="#94a3b8" fontWeight={500}>
                          Nenhuma revenda sem compras encontrada
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginated.map((row, idx) => (
                      <TableRow
                        key={`${row.num_id_revenda}-${idx}`}
                        sx={{
                          transition: "all 0.2s",
                          "&:hover": { bgcolor: colorPrimary + "08" },
                          "& td": { borderBottom: "1px solid #f8fafc" },
                        }}
                      >
                        <TableCell sx={{ fontSize: "0.85rem", fontWeight: 700, color: "#1e293b", py: 2.2 }}>
                          #{row.num_id_revenda}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#1e293b", py: 2.2 }}>
                          {row.revenda_label}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.85rem", color: "#64748b", py: 2.2, whiteSpace: "nowrap" }}>
                          {row.documento}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.85rem", color: "#64748b", py: 2.2, whiteSpace: "nowrap" }}>
                          {row.contato}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.85rem", color: "#64748b", py: 2.2 }}>
                          {row.email}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.85rem", color: "#64748b", py: 2.2, whiteSpace: "nowrap" }}>
                          {row.local}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Box
              px={3.5}
              py={2.5}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              borderTop="1px solid #f1f5f9"
              bgcolor="#fafbfc"
            >
              <Typography fontSize="0.82rem" color="#94a3b8" fontWeight={500}>
                Mostrando {paginated.length} de {revendas.length} resultados
              </Typography>
              <Box display="flex" alignItems="center" gap={0.5}>
                <IconButton
                  size="small"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  sx={{ color: "#94a3b8" }}
                >
                  <ChevronLeft />
                </IconButton>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let page: number;
                  if (totalPages <= 5) {
                    page = i + 1;
                  } else if (currentPage <= 3) {
                    page = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    page = totalPages - 4 + i;
                  } else {
                    page = currentPage - 2 + i;
                  }
                  return (
                    <Box
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.8rem",
                        fontWeight: 700,
                        cursor: "pointer",
                        transition: "all 0.2s",
                        ...(currentPage === page
                          ? {
                              bgcolor: colorPrimary,
                              color: "white",
                              boxShadow: `0 2px 8px ${colorPrimary}40`,
                            }
                          : {
                              color: "#64748b",
                              "&:hover": { bgcolor: "#f1f5f9" },
                            }),
                      }}
                    >
                      {page}
                    </Box>
                  );
                })}
                <IconButton
                  size="small"
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  sx={{ color: "#94a3b8" }}
                >
                  <ChevronRight />
                </IconButton>
              </Box>
            </Box>
          </Paper>
        </AnimatedBox>
      </Box>
    </Fade>
  );
}
